import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { useAuth } from '@/hooks/useAuth';
import { usePlan } from '@/hooks/usePlan';
import { FloppyDisk, UploadSimple, X, Lock, Trash, DotsSixVertical } from '@phosphor-icons/react';
import { optimizeImage } from '@/lib/imageOptimizer';
import { PublicationHistory } from './PublicationHistory';
import { AdminDomainSettings } from './AdminDomainSettings';
import { AdminSubdomainChange } from './AdminSubdomainChange';
import { UpgradeModal } from './UpgradeModal';

interface SiteSettings {
  id: string;
  site_title: string;
  site_description: string;
  favicon_url: string | null;
  social_image_url: string | null;
  ga_measurement_id: string | null;
  dark_mode_enabled: boolean;
  dark_bg_color: string;
  dark_text_color: string;
  show_badge: boolean;
  footer_links: { label: string; url: string }[] | null;
}

const monoFont = "'IBM Plex Mono', monospace";

export function AdminSettings() {
  const { session } = useAuth();
  const { isPro, isFree, isCustom } = usePlan();
  const [settings, setSettings] = useState<SiteSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [uploadingFavicon, setUploadingFavicon] = useState(false);
  const [uploadingOG, setUploadingOG] = useState(false);
  const [showUpgrade, setShowUpgrade] = useState(false);
  const [dragIndex, setDragIndex] = useState<number | null>(null);
  const faviconInputRef = useRef<HTMLInputElement>(null);
  const ogInputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    const load = async () => {
      if (!session) return;
      let { data } = await supabase
        .from('site_settings')
        .select('id, site_title, site_description, favicon_url, social_image_url, ga_measurement_id, dark_mode_enabled, dark_bg_color, dark_text_color, show_badge, footer_links')
        .eq('user_id', session.user.id)
        .limit(1)
        .maybeSingle();
      if (!data) {
        const { data: created } = await supabase.from('site_settings').insert({ user_id: session.user.id } as any).select('id, site_title, site_description, favicon_url, social_image_url, ga_measurement_id, dark_mode_enabled, dark_bg_color, dark_text_color, show_badge, footer_links').single();
        data = created;
      }
      if (data) {
        setSettings(data as unknown as SiteSettings);
      }
      setLoading(false);
    };
    load();
  }, [session]);

  const update = (key: keyof Omit<SiteSettings, 'id' | 'dark_mode_enabled' | 'dark_bg_color' | 'dark_text_color'>, value: string | null) => {
    if (!settings) return;
    setSettings({ ...settings, [key]: value });
    setSaved(false);
  };

  const save = async () => {
    if (!settings) return;
    setSaving(true);
    const { id, dark_mode_enabled, dark_bg_color, dark_text_color, ...rest } = settings;
    await supabase
      .from('site_settings')
      .update({ ...rest, updated_at: new Date().toISOString() } as any)
      .eq('id', id);
    setSaving(false);
    setSaved(true);
    setTimeout(() => setSaved(false), 2000);
  };

  const handleFaviconUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !settings) return;
    setUploadingFavicon(true);
    const userId = session?.user?.id;
    if (!userId) { setUploadingFavicon(false); return; }
    const ext = file.name.split('.').pop();
    const path = `${userId}/favicons/${Date.now()}.${ext}`;
    const { error } = await supabase.storage.from('site-assets').upload(path, file);
    if (!error) {
      const { data } = supabase.storage.from('site-assets').getPublicUrl(path);
      update('favicon_url', data.publicUrl);
    }
    setUploadingFavicon(false);
    e.target.value = '';
  };

  const handleOGUpload = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file || !settings) return;
    setUploadingOG(true);
    const userId = session?.user?.id;
    if (!userId) { setUploadingOG(false); return; }
    const result = await optimizeImage(file);
    const ext = result.file.name.split('.').pop();
    const path = `${userId}/og-images/${Date.now()}.${ext}`;
    const { error } = await supabase.storage.from('site-assets').upload(path, result.file);
    if (!error) {
      const { data } = supabase.storage.from('site-assets').getPublicUrl(path);
      update('social_image_url', data.publicUrl);
    }
    setUploadingOG(false);
    e.target.value = '';
  };


  const footerLinks = Array.isArray(settings?.footer_links) ? settings!.footer_links! : [];

  const setFooterLinks = (links: { label: string; url: string }[]) => {
    if (!settings) return;
    setSettings({ ...settings, footer_links: links });
    setSaved(false);
  };

  const isValidUrl = (url: string) => url === '' || /^https?:\/\//i.test(url);

  if (loading || !settings) return <p style={{ fontFamily: monoFont }}>Carregando...</p>;

  // CMS is always dark
  const fg = 'rgba(247,246,242,0.85)';
  const borderBase = 'rgba(247,246,242,0.15)';
  const borderDash = 'rgba(247,246,242,0.12)';

  const inputStyle: React.CSSProperties = {
    fontFamily: monoFont,
    fontSize: 13,
    fontWeight: 300,
    padding: '6px 10px',
    border: `1px solid ${borderBase}`,
    backgroundColor: 'transparent',
    color: fg,
    width: '100%',
  };

  const titleChars = settings.site_title?.length || 0;
  const descChars = settings.site_description?.length || 0;

  return (
    <div className="max-w-2xl" style={{ color: fg }}>
      <UpgradeModal
        open={showUpgrade}
        onClose={() => setShowUpgrade(false)}
        title="Recurso exclusivo do plano Pro"
        description="O domínio personalizado e o controle do badge estão disponíveis apenas no plano Pro."
      />

      <h2 className="uppercase text-sm font-light tracking-wider mb-8" style={{ fontFamily: monoFont, color: fg }}>
        CONFIGURAÇÕES
      </h2>

      {/* Subdomínio */}
      <AdminSubdomainChange />

      {/* Domínio */}
      {isFree ? (
        <Section title="Domínio Personalizado">
          <button
            onClick={() => setShowUpgrade(true)}
            style={{
              fontFamily: monoFont, fontSize: 12, fontWeight: 300,
              color: 'rgba(247,246,242,0.4)', display: 'flex', alignItems: 'center', gap: 8,
              background: 'none', border: `1px dashed rgba(247,246,242,0.12)`,
              padding: '16px 20px', cursor: 'pointer', width: '100%',
            }}
          >
            <Lock size={14} weight="thin" style={{ color: 'rgba(247,246,242,0.45)' }} />
            Domínio personalizado — exclusivo do plano Pro
          </button>
        </Section>
      ) : (
        <AdminDomainSettings />
      )}

      {/* Badge CRIAAA */}
      <Section title="Badge CRIAAA">
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div>
            <p style={{ fontFamily: monoFont, fontSize: 12, color: fg, marginBottom: 4 }}>
              Exibir badge "criado com CRIAAA"
            </p>
            <p style={{ fontFamily: monoFont, fontSize: 12, color: 'rgba(247,246,242,0.35)' }}>
              {isFree ? 'Disponível apenas no plano Pro' : 'Exibido no canto inferior direito do site público'}
            </p>
          </div>
          {isPro ? (
            <button
              onClick={() => {
                if (!settings) return;
                setSettings({ ...settings, show_badge: !settings.show_badge });
                setSaved(false);
              }}
              style={{
                width: 44, height: 24, borderRadius: 12,
                backgroundColor: settings.show_badge ? '#00E87A' : 'rgba(247,246,242,0.15)',
                border: 'none', cursor: 'pointer', position: 'relative',
                transition: 'background-color 0.2s',
              }}
            >
              <span style={{
                position: 'absolute', top: 2, 
                left: settings.show_badge ? 22 : 2,
                width: 20, height: 20, borderRadius: '50%',
                backgroundColor: '#F7F6F2',
                transition: 'left 0.2s',
              }} />
            </button>
          ) : (
            <Lock size={14} weight="thin" style={{ color: 'rgba(247,246,242,0.45)', cursor: 'pointer' }} onClick={() => setShowUpgrade(true)} />
          )}
        </div>
      </Section>

      {/* Links de rodapé — apenas plano custom */}
      {isCustom && (
        <Section title="Links de Rodapé">
          <div className="flex flex-col gap-3">
            {footerLinks.map((link, i) => (
              <div
                key={i}
                draggable
                onDragStart={() => setDragIndex(i)}
                onDragOver={(e) => e.preventDefault()}
                onDrop={() => {
                  if (dragIndex === null || dragIndex === i) return;
                  const next = [...footerLinks];
                  const [moved] = next.splice(dragIndex, 1);
                  next.splice(i, 0, moved);
                  setDragIndex(null);
                  setFooterLinks(next);
                }}
                onDragEnd={() => setDragIndex(null)}
                style={{ display: 'flex', alignItems: 'center', gap: 8, opacity: dragIndex === i ? 0.4 : 1 }}
              >
                <DotsSixVertical size={16} weight="thin" style={{ color: 'rgba(247,246,242,0.35)', cursor: 'grab', flexShrink: 0 }} />
                <input
                  value={link.label}
                  onChange={(e) => {
                    const next = [...footerLinks];
                    next[i] = { ...next[i], label: e.target.value };
                    setFooterLinks(next);
                  }}
                  placeholder="Nome"
                  style={{ ...inputStyle, flex: 1 }}
                />
                <div style={{ flex: 2 }}>
                  <input
                    value={link.url}
                    onChange={(e) => {
                      const next = [...footerLinks];
                      next[i] = { ...next[i], url: e.target.value };
                      setFooterLinks(next);
                    }}
                    placeholder="https://"
                    style={{
                      ...inputStyle,
                      border: `1px solid ${isValidUrl(link.url) ? borderBase : '#FF3F2D'}`,
                    }}
                  />
                  {!isValidUrl(link.url) && (
                    <span className="text-[9px] mt-1 block" style={{ fontFamily: monoFont, color: '#FF3F2D' }}>
                      a url deve começar com http:// ou https://
                    </span>
                  )}
                </div>
                <button
                  onClick={() => setFooterLinks(footerLinks.filter((_, j) => j !== i))}
                  style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4, color: 'rgba(247,246,242,0.45)', flexShrink: 0 }}
                  aria-label="Remover link"
                >
                  <Trash size={14} weight="thin" />
                </button>
              </div>
            ))}

            <button
              onClick={() => setFooterLinks([...footerLinks, { label: '', url: '' }])}
              disabled={footerLinks.length >= 5}
              className="uppercase text-[10px] px-3 py-2 nav-hover-bg self-start disabled:opacity-30"
              style={{ fontFamily: monoFont, border: `1px solid ${borderBase}`, color: fg, backgroundColor: 'transparent' }}
            >
              + Adicionar Link
            </button>
            <span className="text-[9px] opacity-30 block" style={{ fontFamily: monoFont, color: fg }}>
              Até 5 links exibidos no rodapé do site público. Arraste para reordenar.
            </span>
          </div>
        </Section>
      )}

      {/* SEO */}
      <Section title="SEO — Otimização para Buscadores">
        <Field label={`Título do site (${titleChars}/60)`}>
          <input
            value={settings.site_title || ''}
            onChange={(e) => update('site_title', e.target.value.slice(0, 60))}
            style={inputStyle}
            placeholder="CRIAAA — Portfolio"
            maxLength={60}
          />
          <span className="text-[9px] opacity-30 mt-1 block" style={{ fontFamily: monoFont, color: fg }}>
            Aparece no título da aba do navegador e em resultados de busca.
          </span>
        </Field>

        <Field label={`Descrição do site (${descChars}/160)`}>
          <textarea
            value={settings.site_description || ''}
            onChange={(e) => update('site_description', e.target.value.slice(0, 160))}
            style={{ ...inputStyle, minHeight: 80, resize: 'vertical' }}
            placeholder="criaaa.com — Portfolio de fotografia contemporânea"
            maxLength={160}
          />
          <span className="text-[9px] opacity-30 mt-1 block" style={{ fontFamily: monoFont, color: fg }}>
            Resumo que aparece abaixo do título em resultados do Google.
          </span>
        </Field>

        {/* SEO Preview */}
        <div className="mt-4 p-4" style={{ border: `1px dashed ${borderDash}` }}>
          <p className="text-[9px] uppercase opacity-40 mb-2" style={{ fontFamily: monoFont, color: fg }}>
            Pré-visualização nos resultados de busca
          </p>
          <div>
            <div className="text-[11px] mb-1" style={{ fontFamily: monoFont, color: '#1a0dab', opacity: 0.7 }}>
               criaaa.com
            </div>
            <div className="text-sm mb-1" style={{ fontFamily: 'Arial, sans-serif', color: '#1a0dab', textDecoration: 'underline' }}>
              {settings.site_title || 'CRIAAA — Portfolio'}
            </div>
            <div className="text-xs opacity-60" style={{ fontFamily: 'Arial, sans-serif', color: '#4d5156', lineHeight: 1.4 }}>
              {settings.site_description || 'criaaa.com — Portfolio de fotografia contemporânea'}
            </div>
          </div>
        </div>
      </Section>

      {/* Favicon */}
      <Section title="Favicon">
        <Field label="Ícone da aba do navegador">
          <div className="flex items-center gap-3">
            {settings.favicon_url && (
              <div className="relative">
                <img src={settings.favicon_url} alt="Favicon" className="w-8 h-8 object-contain" style={{ border: `1px solid ${borderBase}` }} />
                <button
                  onClick={() => update('favicon_url', null)}
                  className="absolute -top-1 -right-1 w-4 h-4 flex items-center justify-center bg-foreground text-background text-[10px]"
                >
                  <X size={10} />
                </button>
              </div>
            )}
            <button
              onClick={() => faviconInputRef.current?.click()}
              className="uppercase text-[10px] px-3 py-2 nav-hover-bg"
              style={{ fontFamily: monoFont, border: `1px solid ${borderBase}`, color: fg }}
              disabled={uploadingFavicon}
            >
              <UploadSimple size={12} weight="thin" className="inline mr-1" />
              {uploadingFavicon ? 'Enviando...' : settings.favicon_url ? 'Trocar Favicon' : 'Enviar Favicon'}
            </button>
            <input ref={faviconInputRef} type="file" accept=".ico,.png,.svg" className="hidden" onChange={handleFaviconUpload} />
          </div>
          <span className="text-[9px] opacity-30 mt-1 block" style={{ fontFamily: monoFont, color: fg }}>
            Aparece na aba do navegador. Recomendado: 32×32px ou 64×64px. Formatos: ICO, PNG, SVG.
          </span>
        </Field>
      </Section>

      {/* Social Image */}
      <Section title="Imagem de Compartilhamento">
        <Field label="Open Graph / Twitter Card">
          <div className="flex flex-col gap-3">
            {settings.social_image_url && (
              <div className="relative inline-block">
                <img src={settings.social_image_url} alt="Social" className="max-w-sm h-auto" style={{ border: `1px solid ${borderBase}` }} />
                <button
                  onClick={() => update('social_image_url', null)}
                  className="absolute top-2 right-2 w-6 h-6 flex items-center justify-center bg-foreground text-background"
                >
                  <X size={14} />
                </button>
              </div>
            )}
            <div className="flex items-center gap-3">
              <button
                onClick={() => ogInputRef.current?.click()}
                className="uppercase text-[10px] px-3 py-2 nav-hover-bg"
                style={{ fontFamily: monoFont, border: `1px solid ${borderBase}`, color: fg }}
                disabled={uploadingOG}
              >
                <UploadSimple size={12} weight="thin" className="inline mr-1" />
                {uploadingOG ? 'Enviando...' : settings.social_image_url ? 'Trocar Imagem' : 'Enviar Imagem'}
              </button>
              <input ref={ogInputRef} type="file" accept="image/*" className="hidden" onChange={handleOGUpload} />
            </div>
          </div>
          <span className="text-[9px] opacity-30 mt-1 block" style={{ fontFamily: monoFont, color: fg }}>
            Aparece ao compartilhar o site em redes sociais e mensagens. Recomendado: 1200×630px. Formatos: JPG, PNG, WEBP.
          </span>
        </Field>
      </Section>

      {/* Google Analytics */}
      <Section title="Google Analytics">
        <Field label="Measurement ID">
          <input
            value={settings.ga_measurement_id || ''}
            onChange={(e) => {
              const val = e.target.value.trim();
              // Basic validation: allow empty or G- followed by alphanumerics
              if (val === '' || /^G-[A-Z0-9]+$/i.test(val)) {
                update('ga_measurement_id', val || null);
              }
            }}
            style={inputStyle}
            placeholder="G-XXXXXXXXXX"
          />
          <span className="text-[9px] opacity-30 mt-1 block" style={{ fontFamily: monoFont, color: fg }}>
            Cole o código G- fornecido pelo Google Analytics 4. Deixe em branco para desabilitar.
          </span>
        </Field>
      </Section>

      {/* Histórico de publicações */}
      <div style={{ borderTop: '0.5px solid rgba(247,246,242,0.08)', marginTop: 32, paddingTop: 32 }}>
        <PublicationHistory />
      </div>

      {/* STICKY SAVE BAR */}
      <div
        className="sticky bottom-0"
        style={{
          backgroundColor: 'hsl(var(--cms-bg))',
          paddingTop: 24,
          marginTop: 24,
        }}
      >
        <div style={{ borderTop: `1px solid ${borderDash}`, padding: '16px 0' }}>
          <button
            onClick={save}
            disabled={saving}
            className="uppercase text-xs py-3 px-6 w-full flex items-center justify-center gap-2 disabled:opacity-50"
            style={{
              border: `1px solid ${fg}`,
              fontFamily: monoFont,
              color: fg,
              backgroundColor: 'transparent',
              transition: 'opacity 0.2s',
            }}
          >
            <FloppyDisk size={14} weight="thin" />
            {saving ? 'Salvando...' : saved ? 'Salvo ✓' : 'Salvar Configurações'}
          </button>
        </div>
      </div>
    </div>
  );
}

function Section({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div className="mb-8">
      <h3 className="text-[11px] uppercase tracking-wider mb-4" style={{ fontFamily: monoFont, color: 'rgba(247,246,242,0.45)' }}>
        {title}
      </h3>
      <div className="flex flex-col gap-4">{children}</div>
    </div>
  );
}

function Field({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <label className="text-[10px] uppercase" style={{ fontFamily: monoFont, color: 'rgba(247,246,242,0.45)' }}>
        {label}
      </label>
      {children}
    </div>
  );
}
